// src/services/supabase/invites.js
// Servicios para gestión de invitaciones

import { supabase } from '../../supabaseClient';

/**
 * Obtiene todas las invitaciones de todas las propiedades (Admin)
 * @returns {Promise<Array>} Lista de invitaciones ordenadas por fecha de creación
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const getInvites = async () => {
  try {
    const { data, error } = await supabase
      .from('invites')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Convertir formato
    return data.map(i => ({
      id: i.id,
      propertyId: i.property_id,
      name: i.name,
      email: i.email,
      phone: i.phone,
      token: i.token,
      createdAt: i.created_at,
      canBlindBid: i.can_blind_bid || false,
      hasAccess: i.has_access !== false, // default true
    }));
  } catch (error) {
    console.error('Error obteniendo invitaciones:', error);
    throw error;
  }
};

/**
 * Obtiene todas las invitaciones de una propiedad específica
 * @param {string} propertyId - UUID de la propiedad
 * @returns {Promise<Array>} Lista de invitaciones de la propiedad
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const getPropertyInvites = async (propertyId) => {
  try {
    const { data, error } = await supabase
      .from('invites')
      .select('*')
      .eq('property_id', propertyId)
      .order('created_at', { ascending: false});

    if (error) throw error;
    
    return data.map(i => ({
      id: i.id,
      propertyId: i.property_id,
      name: i.name,
      email: i.email,
      phone: i.phone,
      token: i.token,
      createdAt: i.created_at,
      canBlindBid: i.can_blind_bid || false,
      hasAccess: i.has_access !== false, // default true
    }));
  } catch (error) {
    console.error('Error obteniendo invitaciones de propiedad:', error);
    throw error;
  }
};

/**
 * Verifica si un token de invitación es válido para una propiedad
 * Usado para autenticar compradores via magic link
 * @param {string} propertyId - UUID de la propiedad
 * @param {string} token - Token único de la invitación
 * @returns {Promise<Object|null>} Invitación válida o null si no existe
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const verifyInvite = async (propertyId, token) => {
  try {
    const { data, error } = await supabase
      .from('invites')
      .select('*')
      .eq('property_id', propertyId)
      .eq('token', token)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No encontrado
      throw error;
    }
    
    return {
      id: data.id,
      propertyId: data.property_id,
      name: data.name,
      email: data.email,
      phone: data.phone,
      token: data.token,
      createdAt: data.created_at,
      pinVerified: data.pin_verified || false,
      canBlindBid: data.can_blind_bid || false,
      hasAccess: data.has_access !== false, // default true
    };
  } catch (error) {
    console.error('Error verificando invitación:', error);
    throw error;
  }
};

/**
 * Crea una nueva invitación para un comprador
 * @param {Object} inviteData - Datos de la invitación
 * @param {string} inviteData.propertyId - UUID de la propiedad
 * @param {string} inviteData.name - Nombre del invitado
 * @param {string} inviteData.email - Email del invitado
 * @param {string} inviteData.token - Token único generado
 * @param {string} [inviteData.phone] - Teléfono opcional
 * @returns {Promise<Object>} Invitación creada
 * @throws {Error} Si hay error al crear en Supabase
 */
export const createInvite = async (inviteData) => {
  try {
    const { data, error } = await supabase
      .from('invites')
      .insert([
        {
          property_id: inviteData.propertyId,
          name: inviteData.name,
          email: inviteData.email,
          phone: inviteData.phone || null,
          token: inviteData.token,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creando invitación:', error);
    throw error;
  }
};

/**
 * Elimina una invitación permanentemente (hard delete)
 * @param {string} inviteId - UUID de la invitación
 * @returns {Promise<boolean>} true si se eliminó correctamente
 * @throws {Error} Si hay error al eliminar en Supabase
 */
export const deleteInvite = async (inviteId) => {
  try {
    const { error } = await supabase
      .from('invites')
      .delete()
      .eq('id', inviteId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error eliminando invitación:', error);
    throw error;
  }
};

/**
 * Actualizar una invitación existente
 */
export const updateInvite = async (inviteId, updates) => {
  try {
    const updateData = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.phone !== undefined) updateData.phone = updates.phone;

    const { data, error } = await supabase
      .from('invites')
      .update(updateData)
      .eq('id', inviteId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error actualizando invitación:', error);
    throw error;
  }
};

/**
 * Conceder o revocar acceso a un invitado
 * @param {string} inviteId - ID de la invitación
 * @param {boolean} value - true para conceder acceso, false para bloquearlo
 */
export const setUserAccess = async (inviteId, value) => {
  try {
    const { error } = await supabase
      .from('invites')
      .update({ has_access: value })
      .eq('id', inviteId);

    if (error) throw error;
    console.log(`✅ Acceso ${value ? 'concedido' : 'revocado'} para invitado ${inviteId}`);
  } catch (error) {
    console.error('Error actualizando acceso del invitado:', error);
    throw error;
  }
};

/**
 * Habilitar oferta ciega anticipada para múltiples invitados (operación irreversible)
 * @param {array} inviteIds - Array de IDs de invitaciones
 */
export const setBlindBidAccess = async (inviteIds) => {
  try {
    const { data, error } = await supabase
      .from('invites')
      .update({ can_blind_bid: true })
      .in('id', inviteIds)
      .select();

    if (error) throw error;

    console.log(`✅ Habilitados ${data.length} invitados para oferta ciega anticipada`);
    return data;
  } catch (error) {
    console.error('Error actualizando acceso a oferta ciega:', error);
    throw error;
  }
};

